import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { Component, Input } from "@angular/core";
import { ConfirmationService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { SubSink } from "subsink";
import { PpsUtilityService } from "../services/pps-utility.service";
import { WorkAreaService } from "../services/work-area.service";
import { EmployeeBase } from "../shared/models/employee-base";
import { IWorkArea } from "../shared/models/workarea";
import { ToastService } from "../shared/toast/toast.service";
import { DeleteActionComponent } from "./delete-action.component";
import { WorkareaFormComponent } from "./workarea-form.component";

@Component({
  selector: "pps-manage-work-area",
  templateUrl: "./manage-work-area.component.html"
})
export class ManageWorkAreaComponent{
  private subs = new SubSink();
  smallScreen: boolean = false;

  @Input() workAreas: IWorkArea[] = [];
  @Input() employees: EmployeeBase[] = [];

  private gridApi;
  private gridColumnApi;
  gridHeaders: any[];
  context:any = {};
  gridOptions: any = {
    getRowId: param => {
      let workarea = param.data as IWorkArea;
      return `${workarea.id}`;
    },
    defaultColDef:{
      resizable: true
    }
  };  

  constructor(
    private _workAreaService: WorkAreaService,
    private _toast: ToastService,
    private _confirm: ConfirmationService,
    private _util: PpsUtilityService,
    private _dialog: DialogService,
    private breakpointObserver: BreakpointObserver){
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });      
  }

  ngOnInit(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
  }
  
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.api;
  }

  onRowDblClicked(params){
    let workarea = params.data as IWorkArea;
    this.addOrUpdateWorkArea(workarea);
  }

  removable(workArea: IWorkArea){
    return workArea.usage === 0;
  }

  addOrUpdateWorkArea(workArea: IWorkArea){
    workArea = workArea || {
      id: 0,
      areaName: "",
      sortNo: 0,
      teamLeaders: []
    };

    let dialog = this._dialog.open(WorkareaFormComponent, {
      header: workArea.id === 0 ? "Add work area" : "Update work area",
      styleClass: "size-md",
      data:{
        workArea: workArea,
        employees: this.employees
      },
      draggable: true
    });

    this.subs.sink = dialog.onClose.subscribe(
      workArea => {
        if (workArea){
          this.save(workArea);
        }
      }
    );    
  }

  save(workArea: IWorkArea){
    let isAdd = workArea.id === 0;
    workArea.color = workArea.color || "#ffffff";
    workArea.signOnSheet = workArea.signOnSheet == null ? false : workArea.signOnSheet;
    this.subs.sink = this._workAreaService.add(workArea)
      .subscribe(res => {
        if (res.succeed){
          if (isAdd){
            this.workAreas.unshift(res.result);
          }else{
            var foundIndex = this.workAreas.findIndex(x => x.id === res.result.id);
            this.workAreas[foundIndex] = res.result;
          }

          if (this.gridApi) {
            this.gridApi.setGridOption('rowData', this.workAreas);
          }
          let rowNode = this.gridApi.getRowNode(res.result.id);
          if (rowNode){
            //this.gridApi.refreshCells({force: true, rowNodes: [rowNode]});
            this.gridApi.flashCells({rowNodes: [rowNode]});
            rowNode.setSelected(true);
          }
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
      });
  }

  delete(workArea: IWorkArea){
    if (workArea === null){
      return;
    }

    this._confirm.confirm({
      key: "rg-dialog",
      header: `Delete work area`,
      message: `Please confirm that you want to delete [${workArea.areaName}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteHelper(workArea)
    });
  }

  deleteHelper(workArea: IWorkArea){
    this.subs.sink = this._workAreaService.delete(workArea.id)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          var foundIndex = this.workAreas.findIndex(x => x.id == workArea.id);
          if (foundIndex !== -1 ){
            this.workAreas.splice(foundIndex, 1);
            if (this.gridApi) {
              this.gridApi.setGridOption('rowData', this.workAreas);
            }
            this._toast.succeeded(`[${workArea.areaName}] deleted!`);
          }else{
            this._toast.failed(`Delete [${workArea.areaName}] failed!`);
          }
        }else{
          this._toast.failed(`Failed to delete [${workArea.areaName}]: ${apiResponse.errors.join("|")}`);
        }
      });
  }

  private getColumnDefs(){
    let cols = [
      {
        headerName: "",
        field: "action",
        cellRenderer: DeleteActionComponent,
        width: 50,
        hide: false,
        filter: false,        
        suppressMovable: true,
        cellStyle: {textAlign:"center"}
      },
      {
        colId: "areaName",
        headerName: 'Work Area',
        field:'areaName',
        filter: true,
        floatingFilter: true,
        width: 300,
        sortable: true,
        headerClass: "ag-ext-header-align-left"
      },
      {
        colId: "teamLeaders",
        headerName: 'Responsible Team Leaders',
        field:'teamLeaders',
        filter: true,
        floatingFilter: true,
        flex: 1,
        editable: false,
        sortable: true,
        valueGetter: this.getTeamLeaders,
        headerClass: "ag-ext-header-align-left"
      },
      {
        colId: "color",
        headerName: 'Heading Color',
        field:'color',
        filter: false,
        floatingFilter: false,
        width: 150,
        sortable: true,
        cellStyle: (param) => {
          return {
            backgroundColor: (param.data as IWorkArea).color,
            color:(param.data as IWorkArea).color
          };
        },
        headerClass: "ag-ext-header-align-left"
      },
      {
        colId: "signOnSheet",
        headerName: 'Should Print Signon Sheet',
        field:'signOnSheet',
        filter: false,
        floatingFilter: false,
        width: 150,
        sortable: true,
        valueGetter: this.shouldPrintSignOnSheet,
        headerClass: "ag-ext-header-align-left"
      },
      {
        colId: "usage",
        headerName: 'Employee Count',
        field:'usage',
        filter: true,
        floatingFilter: true,
        width: 200,
        editable: false,
        sortable: true,
        headerClass: "ag-ext-header-align-left"
      }
    ];

    return cols;
  }

  private getTeamLeaders(params){
    let _this = params.context.componentParent;
    let workArea = params.data as IWorkArea;
    return workArea.teamLeaders.map(t => _this._util.getEmployeeName(t)).join("; ");
  }

  private shouldPrintSignOnSheet(params){
    let _this = params.context.componentParent;
    let workArea = params.data as IWorkArea;
    return workArea.signOnSheet ? "Yes" : "No";
  }
}